import React, { useState } from 'react';
import { PaymentRowMulti } from '../PaymentRow/PaymentRowMulti';
import { SmartAllocationModal } from '../SmartAllocationModal';
import { AcceptPaymentModal } from '../AcceptPaymentModal';
import { AcceptanceDetailsModal } from '../AcceptanceDetailsModal';
import type { Payment, AffairePaymentStatsEnhanced } from '../../types';
import { paymentApi } from '../../api/payment.api';

// Re-export for backward compatibility
export type AffairePaymentStats = AffairePaymentStatsEnhanced;

interface PaymentTableProps {
  payments: Payment[];
  affairePaymentStats?: Record<string, AffairePaymentStats>; // Backend stats for multi-payment indicator
  onUpdateAffaire: (paymentId: number, affaireId: string) => void;
  onValidate: (paymentId: number, validate: boolean) => void;
  isLoading?: boolean;
  onRefresh?: () => void;
  onIgnore?: (paymentId: number, isIgnored: boolean) => void;
  onFilterByAffaire?: (affaireId: string) => void;
}

export const PaymentTable: React.FC<PaymentTableProps> = ({
  payments,
  affairePaymentStats,
  onUpdateAffaire,
  onValidate,
  isLoading,
  onRefresh,
  onIgnore,
  onFilterByAffaire
}) => {
  const [selectedPayment, setSelectedPayment] = useState<Payment | null>(null);
  const [showSmartModal, setShowSmartModal] = useState(false);
  const [showAcceptModal, setShowAcceptModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedAffaire, setSelectedAffaire] = useState<any>(null);
  const [expandedPaymentId, setExpandedPaymentId] = useState<number | null>(null);

  const handleToggleExpanded = (paymentId: number) => {
    setExpandedPaymentId(prev => prev === paymentId ? null : paymentId);
  };

  const handleSmartAllocate = (payment: Payment) => {
    setSelectedPayment(payment);
    setShowSmartModal(true);
  };

  const handleSmartSuccess = () => {
    setShowSmartModal(false);
    setSelectedPayment(null);
    if (onRefresh) {
      onRefresh();
    }
  };

  const handleAcceptPayment = (payment: Payment, affaire?: any) => {
    setSelectedPayment(payment);
    setSelectedAffaire(affaire);
    
    // Check if this is already accepted - if so, show details modal instead
    if (affaire?.is_accepted) {
      setShowDetailsModal(true);
    } else {
      setShowAcceptModal(true);
    }
  };

  const handleAcceptSubmit = async (paymentId: number, affaireId: string, note: string) => {
    await paymentApi.acceptPayment({
      payment_id: paymentId,
      affaire_id: affaireId,
      acceptance_note: note
    });
    setShowAcceptModal(false);
    setSelectedPayment(null);
    setSelectedAffaire(null);
    if (onRefresh) {
      onRefresh();
    }
  };

  const handleUnacceptPayment = async (paymentId: number, affaireId: string) => {
    try {
      await paymentApi.unacceptPayment(paymentId, affaireId);
      if (onRefresh) {
        onRefresh();
      }
    } catch (error) {
      console.error('Error unaccepting payment:', error);
      alert('Erreur lors de l\'annulation de l\'acceptation');
    }
  };
  // Sort payments into four groups:
  // 1. Unvalidated (has affaire_id but not truly validated - includes accepted discrepancies)
  // 2. Unmatched (no affaire_id - needs "Ajouter N°")
  // 3. Validated (PAYE BANQUE status only - not just accepted)
  // 4. Ignored (marked as not part of business)
  const sortedPayments = [...payments].sort((a, b) => {
    // Determine group for each payment
    const getGroup = (payment: any) => {
      if (payment.is_ignored) return 4; // Ignored group (last)
      
      // Check if truly validated (PAYE BANQUE), not just accepted
      const isTrulyValidated = payment.payment_status === 'PAYE BANQUE' || 
        (payment.affaires && payment.affaires.some((aff: any) => aff.payment_status === 'PAYE BANQUE'));
      
      if (isTrulyValidated) return 3; // Truly validated group
      
      if (!payment.affaire_id && (!payment.affaires || payment.affaires.length === 0)) return 2; // Unmatched group
      
      return 1; // Unvalidated group (includes accepted but not paid)
    };
    
    const aGroup = getGroup(a);
    const bGroup = getGroup(b);
    
    if (aGroup !== bGroup) {
      // Sort by group (1 comes first, then 2, then 3, then 4)
      return aGroup - bGroup;
    }
    
    // Within same group, sort by date (newest first)
    return new Date(b.date).getTime() - new Date(a.date).getTime();
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!payments || payments.length === 0) {
    return (
      <div className="text-center py-12">
        <svg 
          className="mx-auto h-12 w-12 text-gray-400"
          fill="none" 
          viewBox="0 0 24 24" 
          stroke="currentColor"
        >
          <path 
            strokeLinecap="round" 
            strokeLinejoin="round" 
            strokeWidth={2} 
            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" 
          />
        </svg>
        <h3 className="mt-2 text-sm font-medium text-gray-900">Aucun paiement</h3>
        <p className="mt-1 text-sm text-gray-500">
          Aucun paiement n'a été trouvé pour les critères sélectionnés.
        </p>
      </div>
    );
  }

  return (
    <>
    <div className="rounded-xl border border-gray-200 bg-white">
      <table className="w-full">
        <thead>
          <tr className="border-b border-gray-200 bg-gray-50/50">
            <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-700 uppercase tracking-wide w-24">
              Date
            </th>
            <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-700 uppercase tracking-wide w-44">
              Nom
            </th>
            <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-700 uppercase tracking-wide w-48">
              Communication
            </th>
            <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-700 uppercase tracking-wide w-36">
              Montant
            </th>
            <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-700 uppercase tracking-wide w-52">
              N° Affaire
            </th>
            <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-700 uppercase tracking-wide w-56">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {sortedPayments.map((payment) => (
            <PaymentRowMulti
              key={payment.id}
              payment={payment}
              onUpdateAffaire={onUpdateAffaire}
              onValidate={onValidate}
              onSmartAllocate={handleSmartAllocate}
              onIgnore={onIgnore}
              onAcceptPayment={handleAcceptPayment}
              onUnacceptPayment={handleUnacceptPayment}
              affairePaymentStats={affairePaymentStats}
              onFilterByAffaire={onFilterByAffaire}
              isExpanded={expandedPaymentId === payment.id}
              onToggleExpanded={() => handleToggleExpanded(payment.id)}
            />
          ))}
        </tbody>
      </table>
    </div>
    
    {/* Smart Allocation Modal */}
    {selectedPayment && (
      <SmartAllocationModal
        isOpen={showSmartModal}
        onClose={() => {
          setShowSmartModal(false);
          setSelectedPayment(null);
        }}
        payment={selectedPayment}
        onSuccess={handleSmartSuccess}
      />
    )}
    
    {/* Accept Payment Modal */}
    {selectedPayment && (
      <AcceptPaymentModal
        isOpen={showAcceptModal}
        onClose={() => {
          setShowAcceptModal(false);
          setSelectedPayment(null);
          setSelectedAffaire(null);
        }}
        payment={selectedPayment}
        affaire={selectedAffaire}
        affairePaymentStats={affairePaymentStats}
        onAccept={handleAcceptSubmit}
      />
    )}

    {/* Acceptance Details Modal */}
    {selectedPayment && (
      <AcceptanceDetailsModal
        isOpen={showDetailsModal}
        onClose={() => {
          setShowDetailsModal(false);
          setSelectedPayment(null);
          setSelectedAffaire(null);
        }}
        payment={selectedPayment}
        affaire={selectedAffaire}
        affairePaymentStats={affairePaymentStats}
        onRevert={handleUnacceptPayment}
      />
    )}
  </>
  );
};